import * as React from 'react';
import { Menu } from '@base-ui/react';
import { cn } from '../../lib/utils';
import type {
  DropdownMenuContentProps,
  DropdownMenuItemIconSlotProps,
  DropdownMenuItemProps,
  DropdownMenuItemTextProps,
  DropdownMenuLabelProps,
  DropdownMenuSeparatorProps,
  DropdownMenuTriggerProps,
} from './types';

const DropdownMenu = Menu.Root;

const DropdownMenuPortal = Menu.Portal;

const DropdownMenuGroup = Menu.Group;

const DropdownMenuRadioGroup = Menu.RadioGroup;

const DropdownMenuTrigger = React.forwardRef<
  HTMLButtonElement,
  DropdownMenuTriggerProps
>(({ asChild, children, render, ...props }, ref) => (
  <Menu.Trigger
    ref={ref}
    render={
      asChild && React.isValidElement(children)
        ? (children as React.ReactElement)
        : render
    }
    {...props}
  >
    {asChild ? null : children}
  </Menu.Trigger>
));
DropdownMenuTrigger.displayName = 'DropdownMenuTrigger';

const DropdownMenuContent = React.forwardRef<
  HTMLDivElement,
  DropdownMenuContentProps
>(
  (
    {
      children,
      className,
      portalContainer,
      positionerClassName,
      align = 'start',
      side = 'bottom',
      ...props
    },
    ref,
  ) => (
    <Menu.Portal container={portalContainer}>
      <Menu.Positioner
        align={align}
        side={side}
        sideOffset={6}
        collisionPadding={0}
        className={cn('eye:isolate eye:z-[1400]', positionerClassName)}
        {...props}
      >
        <Menu.Popup
          ref={ref}
          className={cn(
            'eye:group/combobox-content eye:relative eye:z-[1410] eye:max-h-[320px] eye:max-w-[min(320px,calc(100vw-32px))] eye:min-w-[240px] eye:overflow-x-hidden eye:overflow-y-auto eye:overscroll-none eye:rounded-md eye:border eye:border-grey-40 eye:bg-white eye:py-4xs eye:text-black eye:shadow-popover-content',
            'eye:duration-100 eye:data-closed:animate-out eye:data-closed:fade-out-0 eye:data-closed:zoom-out-95',
            'eye:data-open:animate-in eye:data-open:fade-in-0 eye:data-open:zoom-in-95',
            'eye:data-[side=bottom]:slide-in-from-top-2 eye:data-[side=left]:slide-in-from-right-2 eye:data-[side=right]:slide-in-from-left-2 eye:data-[side=top]:slide-in-from-bottom-2',
            className,
          )}
        >
          {children}
        </Menu.Popup>
      </Menu.Positioner>
    </Menu.Portal>
  ),
);
DropdownMenuContent.displayName = 'DropdownMenuContent';

const DropdownMenuItem = React.forwardRef<
  HTMLElement,
  DropdownMenuItemProps
>(({ className, inset, ...props }, ref) => (
  <Menu.Item
    ref={ref}
    className={cn(
      'eye:relative eye:flex eye:w-full eye:min-h-[44px] eye:min-w-0 eye:cursor-pointer eye:items-center eye:justify-start eye:gap-8 eye:border-0 eye:bg-transparent eye:px-16 eye:py-[6px] eye:text-14 eye:text-black eye:transition-[background-color,color] eye:duration-150 eye:outline-none eye:select-none',
      'eye:hover:bg-grey-10 eye:hover:text-black eye:focus:outline-none eye:focus-visible:outline-none',
      'eye:data-[highlighted]:bg-grey-10 eye:data-[highlighted]:text-black',
      'eye:data-[disabled]:pointer-events-none eye:data-[disabled]:opacity-50',
      inset && 'eye:pl-32',
      className,
    )}
    {...props}
  />
));
DropdownMenuItem.displayName = 'DropdownMenuItem';

const DropdownMenuItemText = ({
  children,
  className,
  ...props
}: DropdownMenuItemTextProps) => (
  <span
    className={cn('eye:block eye:min-w-0 eye:flex-1', className)}
    {...props}
  >
    {children}
  </span>
);
DropdownMenuItemText.displayName = 'DropdownMenuItemText';

const DropdownMenuItemIconSlot = ({
  children,
  className,
  ...props
}: DropdownMenuItemIconSlotProps) => (
  <span
    className={cn('eye:flex eye:h-lh eye:shrink-0 eye:items-center eye:self-center', className)}
    {...props}
  >
    {children}
  </span>
);
DropdownMenuItemIconSlot.displayName = 'DropdownMenuItemIconSlot';

const DropdownMenuLabel = React.forwardRef<
  HTMLDivElement,
  DropdownMenuLabelProps
>(({ className, inset, ...props }, ref) => (
  <Menu.GroupLabel
    ref={ref}
    className={cn(
      'eye:px-16 eye:py-[6px] eye:text-12 eye:font-medium eye:text-grey-100',
      inset && 'eye:pl-32',
      className,
    )}
    {...props}
  />
));
DropdownMenuLabel.displayName = 'DropdownMenuLabel';

const DropdownMenuSeparator = React.forwardRef<
  HTMLDivElement,
  DropdownMenuSeparatorProps
>(({ className, ...props }, ref) => (
  <Menu.Separator
    ref={ref}
    className={cn('eye:my-4 eye:h-px eye:bg-grey-20', className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = 'DropdownMenuSeparator';

export {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuItemIconSlot,
  DropdownMenuItemText,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
};
